import React, { useEffect, useRef } from 'react';

const SIZE = 220;
const STROKE = 14;
const R = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * R;
const START_ANGLE = 220; // degrees from top (clockwise), leaves gap at bottom

function polarToXY(angleDeg, radius) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: SIZE / 2 + radius * Math.cos(rad),
    y: SIZE / 2 + radius * Math.sin(rad)
  };
}

function arcPath(startDeg, endDeg, r) {
  const s = polarToXY(startDeg, r);
  const e = polarToXY(endDeg, r);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}

const TRACK_START = -130; // -130 from top = roughly 7 o'clock
const TRACK_END = 130;    // 130 from top = roughly 5 o'clock
const TRACK_SPAN = 260;   // total degrees

export default function SpeedGauge({ value, maxValue = 1000, unit = 'Mbps', label = '', active = false }) {
  const pct = Math.min(value / maxValue, 1);
  const fillEnd = TRACK_START + pct * TRACK_SPAN;

  const trackPath = arcPath(TRACK_START, TRACK_END, R);
  const fillPath = pct > 0 ? arcPath(TRACK_START, fillEnd, R) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        {/* Track */}
        <path
          d={trackPath}
          fill="none"
          stroke="#252a35"
          strokeWidth={STROKE}
          strokeLinecap="round"
        />
        {/* Fill */}
        {fillPath && (
          <path
            d={fillPath}
            fill="none"
            stroke={active ? '#f47b20' : '#c4601a'}
            strokeWidth={STROKE}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.3s ease, d 0.1s linear', filter: active ? 'drop-shadow(0 0 6px rgba(244,123,32,0.6))' : 'none' }}
          />
        )}
        {/* Center text */}
        <text
          x={SIZE / 2} y={SIZE / 2 - 8}
          textAnchor="middle" dominantBaseline="middle"
          fill={active ? '#f47b20' : '#e6e8eb'}
          fontSize="32"
          fontFamily="'JetBrains Mono', monospace"
          fontWeight="600"
        >
          {value > 0 ? (value >= 100 ? Math.round(value) : value.toFixed(1)) : '—'}
        </text>
        <text
          x={SIZE / 2} y={SIZE / 2 + 22}
          textAnchor="middle" dominantBaseline="middle"
          fill="#8b92a0" fontSize="13"
          fontFamily="'Outfit', sans-serif"
        >
          {unit}
        </text>
        {/* Tick marks */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const deg = TRACK_START + t * TRACK_SPAN;
          const inner = polarToXY(deg, R - STROKE / 2 - 6);
          const outer = polarToXY(deg, R + STROKE / 2 + 4);
          return (
            <line key={i} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
              stroke="#252a35" strokeWidth="1.5" />
          );
        })}
        {/* Max label */}
        <text
          x={SIZE / 2} y={SIZE - 14}
          textAnchor="middle" fill="#5a6070" fontSize="10"
          fontFamily="'JetBrains Mono', monospace"
        >
          {maxValue}+
        </text>
      </svg>
      {label && (
        <div style={{
          fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 600,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8b92a0'
        }}>
          {label}
        </div>
      )}
    </div>
  );
}
